import expandMoreRounded from '@iconify/icons-material-symbols/expand-more-rounded';
import spinnerIcon from '@iconify/icons-material-symbols/progress-activity';
import * as Popover from '@radix-ui/react-popover';
import { t } from 'i18next';
import { type ComponentProps, useState } from 'react';
import type { VariantProps } from '#pie/utils/TailwindUtils';
import { cn, tv } from '#pie/utils/TailwindUtils';
import { Icon } from '../icon/Icon';
import { InputCheckbox } from '../input-checkbox/InputCheckbox';
import { InputSearch } from '../input-search/InputSearch';
import { styles } from '../input-text/InputText';
import { menuStyles } from '../menu/Menu';
import { Stack, StackItem } from '../stack/Stack';

export type MultiDropdownOption = {
  label: string;
  value: string;
};

export interface InputMultiDropdownProps
  extends VariantProps<typeof inputMultiDropdown>,
    Omit<ComponentProps<'button'>, 'onChange' | 'value'> {
  options?: MultiDropdownOption[];
  onChange?: (value: string[]) => void;
  value?: string[];
  placeholder?: string;
  isError?: boolean;
  isLoading?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  name: string;
  ref?: React.Ref<HTMLButtonElement>;
}

export const InputMultiDropdown = function InputMultiDropdown({
  ref,
  className,
  disabled,
  fullWidth,
  isError,
  isLoading,
  name,
  onChange,
  options,
  placeholder = t('common.dropdown.select.placeholder'),
  value,
  ...props
}: InputMultiDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const s = inputMultiDropdown({ disabled, fullWidth, isError });
  const selectedValues = value ?? [];
  const selectedLabels =
    options
      ?.filter((option) => selectedValues.includes(option.value))
      .map((option) => option.label) ?? [];

  const filteredOptions =
    options?.filter((option) =>
      option.label.toLowerCase().includes(search.toLowerCase()),
    ) ?? [];

  const allFilteredSelected =
    filteredOptions.length > 0 &&
    filteredOptions.every((option) => selectedValues.includes(option.value));

  const handleSelectAll = () => {
    if (allFilteredSelected) {
      onChange?.(
        selectedValues.filter(
          (v) => !filteredOptions.some((o) => o.value === v),
        ),
      );
    } else {
      const newValues = [
        ...new Set([...selectedValues, ...filteredOptions.map((o) => o.value)]),
      ];
      onChange?.(newValues);
    }
  };

  return (
    <Popover.Root
      modal={false}
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) setSearch('');
      }}
    >
      <Popover.Trigger
        ref={ref}
        className={cn(s.input(), className)}
        role="combobox"
        id={name}
        aria-label={props['aria-label']}
        disabled={disabled || isLoading}
        data-testid={`multidropdown-${name}`}
        {...props}
      >
        <span className={s.value()}>
          {selectedLabels.length > 0 ? (
            selectedLabels.join(', ')
          ) : (
            <span className="text-content-disabled">{placeholder}</span>
          )}
        </span>
        <span className={cn(s.icon(), s.iconEnd())}>
          {isLoading ? (
            <Icon
              role="progressbar"
              aria-label="loading"
              icon={spinnerIcon}
              size={16}
              className="animate-spin"
            />
          ) : (
            <Icon
              icon={expandMoreRounded}
              size={16}
              className={cn(
                'origin-center text-action-secondary-enabled transition-transform motion-reduce:transition-none',
                isOpen && 'rotate-180',
              )}
            />
          )}
        </span>
      </Popover.Trigger>
      <Popover.Content
        className={cn(
          s.content(),
          'flex flex-col p-3 mt-2 max-h-(--radix-popper-available-height) w-(--radix-popper-anchor-width)',
        )}
        align="start"
        side="bottom"
        avoidCollisions={false}
        collisionPadding={16}
      >
        <InputSearch
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('common.search.placeholder')}
          onClear={() => setSearch('')}
          fullWidth
          className="mb-4"
        />
        <Stack
          as="ul"
          gap="sm"
          role="listbox"
          aria-multiselectable="true"
          className="overflow-y-auto"
        >
          {filteredOptions.length > 0 && (
            <StackItem
              as="li"
              className="py-3 pr-3 pl-2 border-b border-border-divider"
            >
              <InputCheckbox
                checked={allFilteredSelected}
                label={t(
                  allFilteredSelected
                    ? 'common.dropdown.deselect_all'
                    : 'common.dropdown.select_all',
                )}
                onCheckedChange={handleSelectAll}
                value="select-all"
              />
            </StackItem>
          )}
          {filteredOptions.map((option) => {
            const checked = selectedValues.includes(option.value);

            return (
              <StackItem as="li" key={option.value} className="py-3 pr-3 pl-2">
                <InputCheckbox
                  checked={checked}
                  label={option.label}
                  onCheckedChange={(nextChecked) => {
                    const nextValues = nextChecked
                      ? [...selectedValues, option.value]
                      : selectedValues.filter((item) => item !== option.value);

                    onChange?.(nextValues);
                  }}
                  value={option.value}
                />
              </StackItem>
            );
          })}
        </Stack>
      </Popover.Content>
    </Popover.Root>
  );
};

const inputMultiDropdown = tv({
  base: '',
  extend: styles,
  slots: {
    content: menuStyles().content(),
    icon: menuStyles().icon(),
    input:
      'data-[state=closed]:border-border-subtle-enabled data-[state=open]:border-border-subtle-enabled flex justify-between px-4 py-3 items-center gap-2',
    value: 'overflow-hidden text-ellipsis whitespace-nowrap text-left',
    isError: 'border-border-input-error focus:ring-border-input-error',
  },
  variants: {
    isError: {
      false: {
        input:
          'focus:border-border-subtle-enabled focus:shadow-none focus:ring-0 data-[state=closed]:focus:border-border-subtle-focus data-[state=open]:focus:border-border-subtle-enabled',
      },
    },
  },
});
